// The one Follow-up Template filler — used by mobile to draft a follow-up and by web to preview a
// template. `{amount}` is only known on the phone at the moment of commitment, so filling happens on
// the client; the server only checks, on save, that a template names no field outside these four.

import { CallOutcome, FollowUpRegister } from '../generated/enums.gen';
import { assertNever } from '../utils/assertNever';

/** The fields a template may use, as written inside the braces. */
export const FOLLOW_UP_FIELDS = ['first_name', 'amount', 'link', 'candidate'] as const;

/** The raw values a template is filled from; the filler applies the fallbacks. */
export interface FollowUpFields {
  /** The donor's first name. Falls back to the first word of `name`, then "there". */
  firstName: string | null;
  /** The donor's full name. */
  name: string | null;
  /** What the donor committed; empty when there's none. */
  amount: number | null;
  /** The resolved donation link; empty when there's none. */
  link: string | null;
  /** Falls back to `campaignName`. */
  candidateName: string | null;
  campaignName: string | null;
}

const present = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

function formatAmount(amount: number | null): string {
  if (amount === null) return '';
  const whole = Number.isInteger(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount);
}

/**
 * Fill `{first_name}`, `{amount}`, `{link}` and `{candidate}`. Any other `{…}` is left exactly as
 * written — the server refuses to save one, so it can only come from an old draft.
 */
export function fillFollowUpTemplate(template: string, fields: FollowUpFields): string {
  const values: Record<(typeof FOLLOW_UP_FIELDS)[number], string> = {
    first_name: present(fields.firstName) ?? present(fields.name)?.split(/\s+/)[0] ?? 'there',
    amount: formatAmount(fields.amount),
    link: present(fields.link) ?? '',
    candidate: present(fields.candidateName) ?? present(fields.campaignName) ?? '',
  };
  return template.replace(/\{(\w+)\}/g, (match, field: string) =>
    field in values ? values[field as keyof typeof values] : match,
  );
}

/**
 * Every distinct `{…}` in a template that isn't one of the four fields, braces included — spaces and
 * case count, so `{ link }` and `{First_Name}` are typos too — then any stray brace (`{link` never
 * closed, `}` alone), which would reach the donor as a literal. The same check the server makes on
 * save; the editor runs it to flag a typo before the Fundraiser hits Save.
 */
export function unknownFollowUpFields(template: string): string[] {
  const rest = FOLLOW_UP_FIELDS.reduce((text, field) => text.split(`{${field}}`).join(''), template);
  const unknown = rest.match(/\{[^{}]*\}/g) ?? [];
  const stray = rest.replace(/\{[^{}]*\}/g, '').match(/[{}]\w*/g) ?? [];
  return [...new Set([...unknown, ...stray])];
}

/**
 * Which follow-up the Caller can send during the call, before any outcome is logged: the donation link
 * ("here's the link I mentioned"). No outcome maps to it.
 */
export function followUpRegisterBeforeOutcome(): FollowUpRegister {
  return FollowUpRegister.DonationLink;
}

/**
 * Whether a register's wording may use `{amount}`. Only the thank-you goes out after a donor commits;
 * every other draft is sent with no amount to fill, so the donor would read a gap — the server refuses
 * to save one there, and the editor flags it.
 */
export function followUpRegisterAllowsAmount(register: FollowUpRegister): boolean {
  switch (register) {
    case FollowUpRegister.ThankYou:
      return true;
    case FollowUpRegister.DonationLink:
    case FollowUpRegister.MissedYou:
    case FollowUpRegister.ThanksForYourTime:
      return false;
    default:
      return assertNever(register);
  }
}

/**
 * Which follow-up a call's outcome gets, or null for none. A wrong number or a removal request gets
 * no follow-up — never message a stranger or someone who asked to be left alone.
 */
export function followUpRegisterFor(outcome: CallOutcome): FollowUpRegister | null {
  switch (outcome) {
    case CallOutcome.Committed:
      return FollowUpRegister.ThankYou;
    case CallOutcome.Voicemail:
    case CallOutcome.NoAnswer:
      return FollowUpRegister.MissedYou;
    case CallOutcome.CallBackRequested:
    case CallOutcome.Declined:
      return FollowUpRegister.ThanksForYourTime;
    case CallOutcome.WrongNumber:
    case CallOutcome.RemovalRequested:
      return null;
    default:
      return assertNever(outcome);
  }
}
